import { useState } from 'react';
import { useForm } from 'react-hook-form';
import TextField from 'UI/components/TextField';
import { AttachedFile } from 'UI/components/Forms/FeedbackForm/Upload/AttachedFile';
import Svg from 'UI/components/Svg';
import { SVG_IMAGES_TYPES } from 'utils/constants';
import {
  useSendContactFormMutation,
  CONTACT_CASH_KEY,
} from 'redux/apis/dataSending';
import classNames from 'classnames';
import { PriceSelector } from './Prices';
import { FormAlert } from './FormAlert';
import useUpload from '../model/use-upload';
import styles from './styles.module.scss';

export const ContactForm = () => {
  const [selectedFiles, setFiles] = useState([]);

  const [sendForm, {
    isSuccess,
    isLoading,
    isError,
  }] = useSendContactFormMutation({ fixedCacheKey: CONTACT_CASH_KEY });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = useForm();

  const {
    handleFilesChange,
    handleOnUnpinFile,
    updateSelectedFileInfo,
  } = useUpload({
    setFiles,
    selectedFiles,
  });

  const submitHandler = handleSubmit(async (values, event) => {
    event.preventDefault();
    const attachments = selectedFiles.map((file) => file.signedUrl);

    await sendForm({
      ...values,
      attachments,
      projectBudget: values.price || '',
      description: values.description,
    });

    if (isSuccess) {
      setFiles([]);
      reset();
    }
  });

  return (
    <>
      <form
        className={classNames(styles.form, { [styles.onSuccess]: isSuccess })}
        onSubmit={submitHandler}
      >
        <div>
          <h4 className={styles['form-title']}>
            Get in touch
          </h4>

          <p className={styles['form-description']}>Share your project goals with us and let&apos;s kick things off.</p>
        </div>

        <div className={styles.formFields}>
          <TextField
            name="name"
            register={register}
            placeholder="Name *"
            errorMessage="Required field"
            required={dirtyFields.name}
            className={classNames(styles.input, styles.imputName)}
          />

          <TextField
            name="phone"
            register={register}
            placeholder="Phone number"
            errorMessage="Incorrect phone number"
            type="tel"
            className={classNames(styles.input, styles.imputPhone)}
          />

          <TextField
            name="email"
            register={register}
            placeholder="Email *"
            errorMessage="Incorrect email address"
            required={dirtyFields?.email}
            type="email"
            className={classNames(styles.input, styles.imputEmail)}
          />

          <TextField
            name="description"
            register={register}
            placeholder="Project Details *"
            errorMessage="Required field"
            required={dirtyFields?.description}
            className={classNames(styles.input, styles.imputDescription)}
            rows={3}
            textarea
          />
        </div>

        <PriceSelector
          register={register}
          setValue={setValue}
          getValues={getValues}
        />

        <div className={styles['form-footer']}>
          <button
            type="submit"
            className={styles['submit-button']}
            disabled={isLoading}
          >
            Send message
          </button>

          <div className={styles.uploadFile}>
            <label htmlFor="file-input">
              <Svg type={SVG_IMAGES_TYPES.attachment} />
              <span>Attach file</span>

              <input
                id="file-input"
                type="file"
                onChange={handleFilesChange}
                multiple
                className={styles.hide}
              />
            </label>
          </div>
        </div>

        {isError && (
          <p className={styles['error-message']}>
            There was an error trying to send your message. Please try again later
          </p>
        )}

        {!!selectedFiles.length && (
          <div className={styles.attachedFiles}>
            {selectedFiles?.map((file) => (
              <AttachedFile
                key={file.signedUrl}
                handleOnUnpinFile={handleOnUnpinFile}
                currentFile={file}
                updateSelectedFileInfo={updateSelectedFileInfo}
              />
            ))}

            {selectedFiles && !!selectedFiles?.length && (
              <div className={styles.moreFilesLink}>
                <label htmlFor="file-input-more">
                  add more files
                  <input
                    id="file-input-more"
                    type="file"
                    onChange={handleFilesChange}
                    multiple
                    className={styles.hide}
                  />
                </label>
              </div>
            )}
          </div>
        )}

      </form>

      <FormAlert />
    </>
  );
};
